import {
  STAGE_LABEL,
  assigneeInSlot,
  inspectorOptions,
  assigneePatch,
  capitalise,
  formatAddress,
  formatPhoneInput,
  inspectionSlots,
  jobAddress,
  jobLocation,
  mayMarkTest,
  nextStep,
  type Assignee,
  type BidType,
  type Contact,
  type Job,
  type JobChecklistItem,
  type JobStage,
  type JobType,
  type StaffRole,
} from "@roof-hawk/core";
import { useState } from "react";

import { AddressInput } from "../../components/AddressInput";
import { AssigneePicker } from "../../components/AssigneePicker";
import { ErrorNote, Row } from "../../components/ui";
import { CallButton } from "../phone/CallButton";
import { t, tn } from "../../i18n";
import {
  fmtDateOnly,
  fmtDateTime,
  fromLocalDateInput,
  streetViewUrl,
  toLocalDateInput,
  zillowUrl,
} from "../../lib/format";
import { navigate } from "../../lib/hooks";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";
import { TextField } from "../contacts/ContactDetail";
import { IntakeCard } from "./IntakeCard";
import { LostPanel } from "./LostPanel";
import { SelectionsCard } from "./SelectionsCard";

/**
 * Customer Info and Job Info.
 *
 * Customer fields write to `contacts` and are shared across every job for that
 * customer; job fields write to `jobs`. The people selectors unlock in stages —
 * an inspector once an inspection is scheduled, a PM and coordinator once the
 * job has been handed to production.
 */
export function OverviewPanel({
  job,
  items,
  onPatchJob,
  onPatchContact,
  jobCount,
}: {
  job: Job;
  items: readonly JobChecklistItem[];
  onPatchJob: (patch: Partial<Job>) => Promise<void>;
  onPatchContact: (patch: Partial<Contact>) => Promise<void>;
  jobCount: number;
  /**
   * Rendered under Customer Info, in the same column. Customer details are
   * short and Job Info is long, which left a tall white nothing down the left
   * of every job. The checklists go there instead, which also puts the thing
   * people actually come to this page for — where the job is up to — near the
   * top rather than below the fold.
   */
}) {
  const { people, crews, leadSources, profileId, roles } = useSession();
  const [confirmBid, setConfirmBid] = useState<BidType | null>(null);
  const contact = job.contact ?? {};

  const approved = Boolean(job.sends_at);

  /**
   * The inspectors — Jonathon, Samuel and Carlos (2026-09-28) — and the
   * crews, plus anybody already on it.
   */
  const assignees = inspectorOptions(
    people,
    crews,
    [
      job.inspector_id,
      job.inspector_crew_id,
      job.inspector_2_id,
      job.inspector_2_crew_id,
    ].filter((id): id is string => Boolean(id)),
  );
  const slots = inspectionSlots(job);
  /** Already in the other slot, so it is not offered twice. */
  const isInSlot = (option: Assignee, index: 0 | 1) => {
    const held = assigneeInSlot(slots[index], assignees);
    return Boolean(held && held.kind === option.kind && held.id === option.id);
  };

  const personSelect = (
    field:
      "rep_id" | "alt_rep_id" | "inspector_id" | "coordinator_id" | "pm_id",
    role: StaffRole,
    disabled = false,
  ) => {
    const held = job[field];
    /*
     * People with this role who are still here, plus whoever the job already
     * holds.
     *
     * A select whose value is absent from its options renders as the first
     * option — "—" — which would read as "this job has no sales rep" while it
     * actually has one, and the next change would be made from that wrong
     * baseline. Somebody who sold a job and has since been retired is exactly
     * that case. The same guard is on the trade select in EstimateCard, and
     * for the same reason.
     */
    const options = people.filter(
      (p) =>
        (p.active !== false &&
          Array.isArray(p.roles) &&
          p.roles.includes(role)) ||
        p.id === held,
    );
    return (
      <select
        value={held ?? ""}
        disabled={disabled}
        onChange={(e) => void onPatchJob({ [field]: e.target.value || null })}
      >
        <option value="">—</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.full_name}
            {p.active === false ? ` ${t("(no longer here)")}` : ""}
          </option>
        ))}
      </select>
    );
  };

  async function changeStage(value: string) {
    if (value === "__auto") {
      await onPatchJob({ stage_manual: false });
      return;
    }
    await onPatchJob({ stage: value as JobStage, stage_manual: true });
    await supabase.from("job_stage_events").insert({
      job_id: job.id,
      stage: value,
      entered_by: profileId,
    });
  }

  /** Changing bid type rebuilds the checklists, so it asks first. */
  async function applyBidChange(value: BidType) {
    const { error } = await supabase.rpc("convert_bid", {
      p_job: job.id,
      p_bid: value,
    });
    setConfirmBid(null);
    if (error) {
      window.alert(error.message);
      return;
    }
    await onPatchJob({ bid_type: value });
    window.location.reload();
  }

  const location = jobLocation(job);

  return (
    <div className="cols">
      <div className="ovcol">
        <div className="block">
          <h2>{t("Customer Info")}</h2>
          {/* The parts, not the joined name — `full_name` is rebuilt from
              these by a database trigger. */}
          <Row label={t("First name")}>
            <TextField
              value={contact.first_name}
              onSave={(v) => void onPatchContact({ first_name: v })}
            />
          </Row>
          <Row label={t("Last name")}>
            <TextField
              value={contact.last_name}
              onSave={(v) => void onPatchContact({ last_name: v })}
            />
          </Row>
          <Row label={t("Company")}>
            <TextField
              value={contact.company}
              onSave={(v) => void onPatchContact({ company: v })}
            />
          </Row>
          {/* Cell first, and the second number says whose it is — the same
              two boxes as the customer's own page. */}
          <Row label={t("Cell")}>
            <TextField
              value={contact.cell}
              width={160}
              format={formatPhoneInput}
              onSave={(v) => void onPatchContact({ cell: v })}
            />
            <CallButton
              number={contact.cell}
              leadSource={job.lead_source}
              jobId={job.id}
              contactId={job.contact_id}
              compact
            />
          </Row>
          <Row label={t("Alternate phone")}>
            <TextField
              value={contact.phone}
              width={160}
              format={formatPhoneInput}
              onSave={(v) => void onPatchContact({ phone: v })}
            />{" "}
            <TextField
              value={contact.phone_label}
              width={150}
              placeholder={t("Whose? e.g. spouse")}
              onSave={(v) => void onPatchContact({ phone_label: v })}
            />
          </Row>
          <Row label={t("Email")}>
            <TextField
              value={contact.email}
              onSave={(v) => void onPatchContact({ email: v })}
            />
          </Row>
          <Row label={t("Mailing Address")}>
            {formatAddress(contact) || "—"}
          </Row>
          <Row label={t("Jobs")}>
            <span
              className="clink"
              onClick={() => navigate(`contact=${job.contact_id}`)}
            >
              {tn(
                jobCount,
                "1 job — view customer →",
                "{n} jobs — view customer →",
              )}
            </span>
          </Row>
          <div className="na-hint" style={{ marginTop: 8 }}>
            {t("These details are shared across every job for this customer.")}
          </div>
        </div>

        {/* What the customer picked — colour first, because that is what
            production asks (ADR 0304). */}
        <SelectionsCard job={job} onPatchJob={onPatchJob} />

        {/* Only on a lead that came through somebody's link; nothing at all
            on a job a rep typed in. */}
        <IntakeCard jobId={job.id} />
      </div>

      <div className="block">
        <h2>{t("Job Info")}</h2>
        <Row label={t("Job Address")}>
          <AddressInput
            value={job.address ?? ""}
            style={{ maxWidth: 165 }}
            onChange={(v) => void onPatchJob({ address: v || null })}
            onPick={(pick) =>
              void onPatchJob({
                address: pick.address,
                city: pick.city,
                state: pick.state,
                zip: pick.zip,
                lat: pick.lat,
                lng: pick.lng,
              })
            }
          />{" "}
          <a
            href={streetViewUrl(location)}
            target="_blank"
            rel="noreferrer"
            className="na-hint"
          >
            Street View ↗
          </a>{" "}
          <a
            href={zillowUrl(jobAddress(job))}
            target="_blank"
            rel="noreferrer"
            className="na-hint"
          >
            Zillow ↗
          </a>
        </Row>
        <Row label={t("City")}>
          <TextField
            value={job.city}
            width={150}
            onSave={(v) => void onPatchJob({ city: v })}
          />
        </Row>
        <Row label={t("State")}>
          <TextField
            value={job.state}
            width={70}
            onSave={(v) => void onPatchJob({ state: v })}
          />
        </Row>
        <Row label={t("Zip")}>
          <TextField
            value={job.zip}
            width={90}
            onSave={(v) => void onPatchJob({ zip: v })}
          />
        </Row>
        <Row label={t("Lead Source")}>
          <select
            value={job.lead_source ?? ""}
            onChange={(e) =>
              void onPatchJob({ lead_source: e.target.value || null })
            }
          >
            <option value="">—</option>
            {[
              ...(job.lead_source && !leadSources.includes(job.lead_source)
                ? [job.lead_source]
                : []),
              ...leadSources,
            ].map((source) => (
              <option key={source} value={source}>
                {t(source)}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t("Job Number")}>{job.job_number}</Row>
        <Row label={t("Job Name")}>
          <TextField
            value={job.job_name}
            onSave={(v) => void onPatchJob({ job_name: v })}
          />
        </Row>
        <Row label={t("Date Created")}>{fmtDateTime(job.created_at)}</Row>
        <Row label={t("Created By")}>{job.creator?.full_name ?? "—"}</Row>
        <Row label={t("Job Type")}>
          <select
            value={job.job_type ?? "residential"}
            onChange={(e) =>
              void onPatchJob({ job_type: e.target.value as JobType })
            }
          >
            <option value="residential">{t("Residential")}</option>
            <option value="commercial">{t("Commercial")}</option>
          </select>
        </Row>
        <Row label={t("Bid Type")}>
          <select
            value={job.bid_type}
            onChange={(e) => setConfirmBid(e.target.value as BidType)}
          >
            {(["repair", "retail", "insurance"] as BidType[]).map((b) => (
              <option key={b} value={b}>
                {t(capitalise(b))}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t("Lead Status")}>
          <select
            value={job.stage_manual ? job.stage : "__auto"}
            onChange={(e) => void changeStage(e.target.value)}
          >
            <option value="__auto">
              {t("Auto ({stage})", {
                stage: t(STAGE_LABEL[job.stage] ?? job.stage),
              })}
            </option>
            {(Object.keys(STAGE_LABEL) as JobStage[]).map((stage) => (
              <option key={stage} value={stage}>
                {t(STAGE_LABEL[stage])}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t("Next Follow-up")}>
          <input
            type="date"
            aria-label={t("Next follow-up date")}
            value={toLocalDateInput(job.follow_up_at)}
            onChange={(e) =>
              void onPatchJob({
                follow_up_at: fromLocalDateInput(e.target.value),
              })
            }
          />
        </Row>
        <Row label={t("Last Contact")}>
          {job.last_contact_at ? (
            fmtDateTime(job.last_contact_at)
          ) : (
            <span className="na-hint">
              {t("No call, text or email logged yet")}
            </span>
          )}
        </Row>
        <Row label={t("Sales Rep")}>{personSelect("rep_id", "sales_rep")}</Row>
        <Row label={t("Alt Rep")}>{personSelect("alt_rep_id", "alt_rep")}</Row>
        {/* Two slots, either of which can be an inspector or a crew. Since
            2026-09-28 the rest of the office is not offered (Jonathon: "only
            me, samuel, and carlos", crews kept). */}
        <Row label={t("Inspection")}>
          <AssigneePicker
            value={assigneeInSlot(inspectionSlots(job)[0], assignees)}
            options={assignees.filter((o) => !isInSlot(o, 1))}
            placeholder={t("Pick an inspector or crew…")}
            onChange={(picked) => void onPatchJob(assigneePatch(1, picked))}
          />
        </Row>
        <Row label={t("Second inspector")}>
          <AssigneePicker
            value={assigneeInSlot(inspectionSlots(job)[1], assignees)}
            options={assignees.filter((o) => !isInSlot(o, 0))}
            placeholder={t("Anyone else going…")}
            onChange={(picked) => void onPatchJob(assigneePatch(2, picked))}
          />
        </Row>
        <Row label={t("Project Coordinator")}>
          {personSelect("coordinator_id", "project_coordinator", !approved)}
        </Row>
        <Row label={t("Project Manager")}>
          {personSelect("pm_id", "project_manager", !approved)}
        </Row>
        <Row label={t("Claim #")}>
          <TextField
            value={job.claim_number}
            width={160}
            onSave={(v) => void onPatchJob({ claim_number: v })}
          />
        </Row>
        {job.inspection_at && (
          <Row label={t("Inspection")}>{fmtDateTime(job.inspection_at)}</Row>
        )}
        {job.work_date && (
          <Row label={t("Roof Date")}>{fmtDateOnly(job.work_date)}</Row>
        )}
        {job.inspection_notes && (
          <Row label={t("Inspection Notes")}>{job.inspection_notes}</Row>
        )}

        {/* A practice job. Works exactly like a real one — same screens, same
            triggers, same ladder — and is left out of every figure that counts
            money. The database returns a test job only to somebody holding
            `tester` (ADR 0151), so only they are offered the toggle: anybody
            else would lose the job the moment they flipped it, and the update
            is refused anyway. */}
        {mayMarkTest(roles) && (
          <Row label={t("Test lead")}>
            <button
              className={`tog${job.is_test ? " on" : ""}`}
              title={
                job.is_test
                  ? t(
                      "Kept out of the sold list, the payouts and lifetime value",
                    )
                  : t("Mark this as practice, so it never lands in the numbers")
              }
              onClick={() => void onPatchJob({ is_test: !job.is_test })}
            />
            <span className="na-hint" style={{ marginLeft: 8 }}>
              {job.is_test
                ? t(
                    "Practice only — never counted as a sale, and hidden from everyone without the tester role",
                  )
                : t(
                    "A real job. Turning this on hides it from everyone without the tester role",
                  )}
            </span>
          </Row>
        )}

        <div className="nextstep">
          <span>{t("Next Step")}</span>
          <b>{t(nextStep(items, job))}</b>
        </div>

        <LostPanel job={job} onPatchJob={onPatchJob} />

        <DeleteJobButton job={job} />
      </div>

      {confirmBid && confirmBid !== job.bid_type && (
        <div className="modal-bg" onClick={() => setConfirmBid(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("Change bid type?")}</h3>
            <div className="subtle">
              {t(
                "This rebuilds the checklist for {bid} and resets checklist progress.",
                { bid: t(capitalise(confirmBid)) },
              )}
            </div>
            <div className="actions">
              <button className="btn-ghost" onClick={() => setConfirmBid(null)}>
                {t("Cancel")}
              </button>
              <button
                className="btn-primary"
                onClick={() => void applyBidChange(confirmBid)}
              >
                {t("Change bid type")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Throwing a job away.
 *
 * This one keeps its confirmation, and deliberately: it is outside the undo
 * that covers the order editors, it takes the estimates and orders with it, and
 * nothing puts them back. The database decides whether it is allowed at all —
 * `delete_job` refuses on anything signed, sent, ordered or paid, unless the
 * job is marked as a test — so a mistake here cannot reach a real sale.
 *
 * A test lead goes whole: the job, its estimates, the packets and the practice
 * signature on them, and the customer themselves once they have no other work.
 * Jonathon, 2026-09-01, on the four practice leads he could not get rid of: "i
 * should always be able to delete a test lead regardless. when i delete them i
 * don't want them showing up in a dashboard."
 */
function DeleteJobButton({ job }: { job: Job }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    const name = job.contact?.full_name ?? t("this job");
    if (
      !window.confirm(
        t("Delete job #{number} for {name}?", {
          number: job.job_number,
          name,
        }) +
          "\n\n" +
          t(
            "This removes its estimates, orders and expenses too, and cannot be undone.",
          ) +
          (job.is_test
            ? "\n\n" +
              t(
                "It is a test lead, so {name} goes with it — along with any calls and texts on it — unless they have other work.",
                { name },
              )
            : ""),
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const { error: rpcError } = await supabase.rpc("delete_job", {
      p_job: job.id,
    });
    setBusy(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    navigate("");
  };

  return (
    <div style={{ marginTop: 14 }}>
      {error && <ErrorNote error={error} />}
      <button
        className="addrow ghost"
        disabled={busy}
        onClick={() => void remove()}
      >
        {busy
          ? t("Deleting…")
          : job.is_test
            ? t("Delete this test lead")
            : t("Delete this job")}
      </button>
      <div className="na-hint" style={{ marginTop: 4 }}>
        {job.is_test
          ? t(
              "A test lead always goes, however far it was walked through, and takes its customer off the lists with it.",
            )
          : t(
              "Only while nothing has been signed, sent, ordered or paid. After that, mark it lost instead.",
            )}
      </div>
    </div>
  );
}
